(() => {
  if (window.neutralsProductReady) return;
  window.neutralsProductReady = true;
  const instances = new Map();
  function init(root) {
    if (instances.has(root)) return;
    const events = new AbortController();
    const on = (el, type, fn) => el?.addEventListener(type, fn, { signal: events.signal, passive: type.startsWith('touch') });
    const form = root.querySelector('.np__form');
    if (!form) return;
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    const variants = [...form.querySelectorAll('[data-variant]')];
    const selected = () => variants.find(el => el.type === 'hidden' || el.checked);
    const sizeMessage = root.querySelector('[data-size-message]');
    const message = root.querySelector('[data-cart-message]');
    const add = root.querySelector('[data-add]');
    const addLabel = (() => {
      const existing = add.querySelector('[data-add-label]');
      if (existing) return existing;
      const label = document.createElement('span');
      label.setAttribute('data-add-label', '');
      label.textContent = add.textContent.trim();
      add.textContent = '';
      add.append(label);
      return label;
    })();
    const native = root.querySelector('[data-native]');
    const confirmation = root.querySelector('[data-confirmation]');
    const bag = root.querySelector('[data-bag]');
    const bagLabel = bag?.textContent.trim() || 'VIEW CART';
    let cartConfirmed = !bag?.hidden;
    let exitAnimation = null;
    let successTimer = null;
    let addAnimation = null;
    let labelGeneration = 0;
    let successActive = false;
    function revealBag() {
      if (!cartConfirmed || !bag) return;
      confirmation.hidden = false;
      cartConfirmed = true;
      bag.textContent = bagLabel;
      bag.hidden = false;
      bag.setAttribute('aria-hidden', 'false');
    }
    function resetConfirmation(options = {}) {
      clearTimeout(successTimer);
      successTimer = null;
      labelGeneration++;
      addAnimation?.cancel();
      addAnimation = null;
      successActive = false;
      add.removeAttribute('data-success-state');
      add.removeAttribute('aria-disabled');
      const animate = options.animate !== false;
      const finish = () => {
        confirmation.hidden = true;
        confirmation.removeAttribute('data-closing');
        add.hidden = false;
        message.textContent = '';
        message.hidden = false;
        message.removeAttribute('data-success');
        message.removeAttribute('data-error');
        if (cartConfirmed) revealBag();
        else { bag.hidden = true; bag.setAttribute('aria-hidden', 'true'); }
        exitAnimation = null;
        update();
      };
      exitAnimation?.cancel();
      exitAnimation = null;
      if (!animate || confirmation.hidden || reduced.matches || !confirmation.animate) { finish(); return; }
      confirmation.setAttribute('data-closing', '');
      const animation = confirmation.animate(
        [{ opacity: 1, transform: 'translateX(0)' }, { opacity: 0, transform: 'translateX(10px)' }],
        { duration: 180, easing: 'cubic-bezier(.4,0,1,1)', fill: 'forwards' }
      );
      exitAnimation = animation;
      animation.finished.catch(() => {}).finally(() => { if (exitAnimation === animation) finish(); });
    }
    function showConfirmation(text, state = 'success') {
      exitAnimation?.cancel();
      exitAnimation = null;
      confirmation.removeAttribute('data-closing');
      add.hidden = true;
      confirmation.hidden = false;
      message.hidden = false;
      message.textContent = text;
      message.toggleAttribute('data-success', state === 'success');
      message.toggleAttribute('data-error', state === 'error');
      if (state === 'success') cartConfirmed = true;
      bag.textContent = bagLabel;
      bag.hidden = state !== 'success' && !cartConfirmed;
      bag.setAttribute('aria-hidden', String(bag.hidden));
    }
    function replaceAddLabel(text, done = () => {}) {
      const token = ++labelGeneration;
      addAnimation?.cancel();
      addAnimation = null;
      if (addLabel.textContent === text) { done(); return; }
      if (reduced.matches || !addLabel.animate) { addLabel.textContent = text; done(); return; }
      const outgoing = addLabel.animate(
        [{ opacity: 1, transform: 'translateX(0)' }, { opacity: 0, transform: 'translateX(8px)' }],
        { duration: 80, easing: 'cubic-bezier(.4,0,1,1)', fill: 'forwards' }
      );
      addAnimation = outgoing;
      outgoing.finished.catch(() => {}).finally(() => {
        if (token !== labelGeneration) return;
        outgoing.cancel();
        addLabel.textContent = text;
        const incoming = addLabel.animate(
          [{ opacity: 0, transform: 'translateX(-8px)' }, { opacity: 1, transform: 'translateX(0)' }],
          { duration: 120, easing: 'cubic-bezier(.22,1,.36,1)' }
        );
        addAnimation = incoming;
        incoming.finished.catch(() => {}).finally(() => {
          if (token !== labelGeneration) return;
          incoming.cancel();
          addAnimation = null;
          done();
        });
      });
    }
    function showButtonSuccess(text) {
      clearTimeout(successTimer);
      exitAnimation?.cancel();
      exitAnimation = null;
      confirmation.removeAttribute('data-closing');
      confirmation.hidden = false;
      bag.textContent = bagLabel;
      bag.hidden = false;
      bag.setAttribute('aria-hidden', 'false');
      message.hidden = true;
      message.textContent = '';
      successActive = true;
      add.hidden = false;
      add.disabled = true;
      add.setAttribute('aria-disabled', 'true');
      add.setAttribute('data-success-state', '');
      replaceAddLabel('ADDED TO CART');
      successTimer = setTimeout(() => {
        replaceAddLabel(root.dataset.addLabel || 'ADD TO CART', () => {
          successActive = false;
          add.removeAttribute('data-success-state');
          update(false);
        });
      }, 1800);
    }
    on(add, 'pointerdown', event => {
      if (!successActive && !busy) return;
      event.preventDefault();
      event.stopPropagation();
    });
    on(add, 'click', event => {
      if (!successActive && !busy) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    });
    let busy = false;
    let request = null;
    form.noValidate = true;
    const available = () => { const el = selected(); return el ? el.dataset.available === 'true' : variants.some(v => v.dataset.available === 'true'); };
    function update(renderLabel = true) {
      variants.forEach(input => {
        if (input.type !== 'hidden' && input.dataset.available === 'false') {
          input.disabled = true;
          if (input.checked) {
            input.checked = false;
            sizeMessage.textContent = 'This size is sold out. Please select an available size.';
          }
        }
      });
      const el = selected();
      if (el) {
        root.querySelector('[data-sizes]')?.removeAttribute('aria-invalid');
        sizeMessage.textContent = `${el.dataset.title} selected${el.dataset.available === 'true' ? '.' : ' — sold out.'}`;
        root.querySelector('[data-price]').textContent = el.dataset.price;
        const compare = root.querySelector('[data-compare]');
        compare.textContent = el.dataset.compare;
        compare.hidden = !el.dataset.compare;
        const badge = root.querySelector('[data-sale-badge]');
        const price = Number(el.dataset.rawPrice);
        const original = Number(el.dataset.rawCompare);
        const discount = Number.isFinite(price) && Number.isFinite(original) && price >= 0 && original > price
          ? Math.round((original - price) / original * 100) : 0;
        if (badge) {
          badge.hidden = discount <= 0 || !variants.some(v => v.dataset.available === 'true');
          badge.textContent = discount > 0 ? `${root.dataset.saleLabel || 'SALE'} · ${discount}% OFF` : '';
        }
      }
      add.disabled = busy || successActive || !available();
      add.setAttribute('aria-disabled', String(add.disabled));
      if (renderLabel) addLabel.textContent = successActive ? 'ADDED TO CART' : busy ? (root.dataset.addingLabel || 'ADDING…') : !available() ? (root.dataset.soldOutLabel || 'SOLD OUT') : (root.dataset.addLabel || 'ADD TO CART');
    }
    function validate() {
      if (!selected()) {
        sizeMessage.textContent = root.dataset.sizeError || 'Please select a size.';
        root.querySelector('[data-sizes]')?.setAttribute('aria-invalid', 'true');
        const sizes = root.querySelector('[data-sizes]');
        sizes?.scrollIntoView({ block: 'center', behavior: reduced.matches ? 'instant' : 'smooth' });
        (variants.find(v => !v.disabled) || sizes)?.focus({ preventScroll: true });
        return false;
      }
      if (!available()) { sizeMessage.textContent = root.dataset.unavailableMessage || 'This size is sold out.'; return false; }
      return true;
    }
    variants.forEach(el => on(el, 'change', () => {
      root.querySelector('[data-sizes]')?.removeAttribute('aria-invalid');
      sizeMessage.textContent = `${el.dataset.title} selected${el.dataset.available === 'true' ? '.' : ' — sold out.'}`;
      if (bag.hidden) resetConfirmation({ animate: false });
      if (native) native.hidden = true;
      const url = new URL(location.href); url.searchParams.set('variant', el.value); history.replaceState({}, '', url);
      update();
    }));
    async function timedFetch(url, options = {}) {
      request = new AbortController();
      const controller = request;
      const timer = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(url, { ...options, credentials: 'same-origin', signal: controller.signal });
        const body = await response.json().catch(error => { if (controller.signal.aborted) throw error; return {}; });
        return { ok: response.ok, body };
      }
      finally { clearTimeout(timer); }
    }
    const withPreviewContext = value => {
      try {
        const url = new URL(value, location.href);
        const current = new URL(location.href);
        ['preview_theme_id', '_fd', 'pb', 'oseid'].forEach(key => {
          if (current.searchParams.has(key)) url.searchParams.set(key, current.searchParams.get(key));
        });
        return url.origin === current.origin ? `${url.pathname}${url.search}` : url.href;
      } catch { return value; }
    };
    on(form, 'submit', async event => {
      if (busy) { event.preventDefault(); return; }
      if (window.neutralsCartIsChecking?.()) { event.preventDefault(); message.textContent = 'Checkout is being prepared. Please wait.'; return; }
      if (!validate()) { event.preventDefault(); return; }
      if (root.hasAttribute('data-mock-product')) {
        event.preventDefault(); message.textContent = 'Preview only. This mock product cannot be purchased; nothing has been added to a cart.'; return;
      }
      if (event.submitter === native) return; // Explicit native fallback, never an automatic duplicate POST.
      event.preventDefault();
      busy = true; update(); form.setAttribute('aria-busy', 'true');
      if (confirmation.hidden || bag.hidden) resetConfirmation({ animate: false });
      if (native) native.hidden = true;
      // Capture before disabling radios; keep selection fixed throughout the request.
      const data = new FormData(form);
      const sectionId = document.querySelector('[data-cart-drawer]')?.dataset.cartSectionId;
      if (sectionId) data.set('sections', sectionId);
      data.set('sections_url', withPreviewContext(window.Shopify?.routes?.root ? window.Shopify.routes.root + 'cart' : root.dataset.cartUrl));
      const disabled = variants.map(v => v.disabled);
      variants.forEach(v => { v.disabled = true; });
      document.dispatchEvent(new CustomEvent('neutrals:cart-add-start'));
      try {
        const mutate = window.neutralsCartMutation || (task => task());
        const response = await mutate(async () => {
          const result = await timedFetch(withPreviewContext(window.Shopify?.routes?.root ? window.Shopify.routes.root + 'cart/add.js' : root.dataset.addUrl + '.js'), { method: 'POST', body: data, headers: { Accept: 'application/json' } });
          if (result.ok && (result.body.id || result.body.items?.length) && window.neutralsCartUpdated) {
            await window.neutralsCartUpdated(result.body.sections);
            result.rendered = true;
          }
          return result;
        });
        if (!response.ok) {
          const error = response.body;
          message.textContent = typeof error.description === 'string' ? error.description : 'This item could not be added. Please check your size and try again.';
          // Re-read public product availability after an inventory rejection.
          // Do not treat every 422 (for example an excessive cart quantity) as sold out.
          try {
            const current = await timedFetch(root.dataset.productUrl + '.js');
            if (current.ok && Array.isArray(current.body.variants)) variants.forEach((input, i) => {
              const variant = current.body.variants.find(v => String(v.id) === input.value);
              if (!variant) return;
              input.dataset.available = String(variant.available);
              disabled[i] = !variant.available;
              const label = input.closest('.np__size')?.querySelector(':scope > span');
              if (label && !variant.available && !label.querySelector('small')) {
                const status = document.createElement('small'); status.textContent = 'Sold out'; label.append(status);
              } else if (variant.available) label?.querySelector('small')?.remove();
            });
          } catch { /* Keep the error and let Shopify validate any explicit retry. */ }
          if (native) native.hidden = false;
          return;
        }
        if (!response.body.id && !response.body.items?.length) throw new Error('Unconfirmed add');
        showButtonSuccess(root.dataset.successMessage || 'ADDED TO CART');
document.dispatchEvent(new CustomEvent('neutrals:cart-added', { detail: { sections: response.body.sections, rendered: response.rendered } }));

      } catch {
        if (!events.signal.aborted) {
                    showConfirmation('We could not confirm the add. Check your cart before trying again, or use the standard form below.', 'error');
          if (native) native.hidden = false;
        }

      } finally {
        variants.forEach((v, i) => { v.disabled = disabled[i]; });
        busy = false; form.removeAttribute('aria-busy'); update(!successActive); revealBag(); request = null;
        document.dispatchEvent(new CustomEvent('neutrals:cart-add-end'));
      }
    });
    const dialogs = [...root.querySelectorAll('dialog')];
    const returnTargets = new Map();
    const exits = new Map();
    let viewerMotion;
    function closeDialog(dialog) {
      if (dialog.matches('[data-viewer]') && viewerMotion) { viewerMotion.close(); return; }
      if (!dialog.open || exits.has(dialog)) return;
      if (reduced.matches || !dialog.animate) { dialog.close(); return; }
      const animation = dialog.animate([{opacity:getComputedStyle(dialog).opacity,transform:'translateY(0)'},{opacity:0,transform:'translateY(6px)'}],{duration:140,easing:'cubic-bezier(.22,1,.36,1)',fill:'forwards'});
      exits.set(dialog, animation);
      animation.finished.catch(() => {}).finally(() => {
        if (exits.get(dialog) !== animation) return;
        dialog.close(); animation.cancel(); exits.delete(dialog);
      });
    }
    function openDialog(dialog, trigger) {
      if (!dialog?.showModal) return false;
      returnTargets.set(dialog, trigger); dialog.showModal(); dialog.querySelector('[data-close]').focus();
      if (dialog.matches('[data-guide]') && !reduced.matches && dialog.animate) {
        dialog.animate([{opacity:0,transform:'translateY(6px)'},{opacity:1,transform:'translateY(0)'}],{duration:160,easing:'cubic-bezier(.22,1,.36,1)'});
      }
      return true;
    }
    dialogs.forEach(dialog => {
      on(dialog, 'cancel', event => { event.preventDefault(); closeDialog(dialog); });
      on(dialog.querySelector('[data-close]'), 'click', () => closeDialog(dialog));
      on(dialog, 'click', event => {
        if (event.target !== dialog) return;
        const rect = dialog.getBoundingClientRect();
        if (!dialog.matches('[data-guide]') || event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) closeDialog(dialog);
      });
      on(dialog, 'close', () => returnTargets.get(dialog)?.focus({ preventScroll: true }));
    });
    const guide = root.querySelector('[data-guide]');
    const guideTrigger = root.querySelector('[data-guide-trigger]');
    if (guide?.showModal && guideTrigger) {
      guideTrigger.hidden = false;
      root.querySelector('.np__guide-fallback').hidden = true;
      on(guideTrigger, 'click', () => openDialog(guide, guideTrigger));
    }
    const gallery = root.querySelector('[data-gallery]');
    const viewer = root.querySelector('[data-viewer]');
    const viewerImages = root.querySelector('[data-viewer-images]');
    const photos = [...root.querySelectorAll('[data-photo]')];
    const thumbs = [...root.querySelectorAll('[data-thumbnail]')];
    const desktopGallery = matchMedia('(min-width: 900px)');
    const stackedGallery = () => desktopGallery.matches ? root.dataset.desktopMedia !== 'carousel' : root.dataset.mobileMedia === 'stack';
    const indexOf = el => Math.max(0, Math.min(photos.length - 1, Math.round(el.scrollLeft / el.clientWidth)));
    function go(el, index) {
      if (el === gallery && stackedGallery()) return;
      el.scrollTo({ left: el.clientWidth * index, behavior: 'instant' });
    }
    if (gallery) {
      const counter = root.querySelector('[data-counter]'); counter.hidden = false;
      const frames = [...viewerImages.children];
      const previous = root.querySelector('[data-previous]');
      const next = root.querySelector('[data-next]');
      let current = indexOf(gallery), transitioning = false, generation = 0;
      const motions = new Set();
      const gestures = [];
      viewerImages.dataset.controlled = 'true';
      gallery.dataset.controlled = 'true';
      function preload() {
        if (!viewer.open) return;
        [current + 1].forEach(index => {
          const image = frames[index]?.querySelector('img');
          if (image) { image.loading = 'eager'; image.decode?.().catch(() => {}); }
        });
      }
      function announce() {
        counter.textContent = `${current + 1} / ${photos.length}`;
        root.querySelector('[data-viewer-counter]').textContent = `Image ${current + 1} of ${photos.length}`;
        thumbs.forEach((thumb, n) => { if (n === current) thumb.setAttribute('aria-current', 'true'); else thumb.removeAttribute('aria-current'); });
        previous.disabled = current === 0;
        next.disabled = current === photos.length - 1;
      }
      function resetViewer(index) {
        generation++; motions.forEach(animation => animation.cancel()); motions.clear();
        transitioning = false;
        gestures.forEach(reset => reset());
        current = index;
        frames.forEach((frame, n) => { frame.hidden = n !== index; });
        go(gallery, current); announce();
      }
      // Every input shares this state; no input queues another transition.
      async function navigate(target, direct = false) {
        target = Math.max(0, Math.min(photos.length - 1, target));
        if (viewerMotion?.busy || exits.has(viewer) || (transitioning && !direct) || target === current) return;
        if (direct) { resetViewer(target); preload(); return; }
        transitioning = true;
        const token = generation, fullscreen = viewer.open;
        const incoming = fullscreen ? frames[target] : photos[target];
        const outgoing = fullscreen ? frames[current] : photos[current];
        const image = incoming.querySelector('img');
        if (image && !image.complete) {
          image.loading = 'eager';
          try { await image.decode(); } catch { /* Leave the current image visible if loading fails. */ }
        }
        if (token !== generation) return;
        if (exits.has(viewer) || (image && !image.naturalWidth)) { transitioning = false; return; }
        const direction = Math.sign(target - current);
        const animations = [];
        if (fullscreen) incoming.hidden = false;
        else go(gallery, target);
        if (!reduced.matches && incoming.animate) {
          const options = {duration:160,easing:'cubic-bezier(.22,1,.36,1)'};
          if (fullscreen) animations.push(outgoing.animate([{opacity:1,transform:'translateX(0)'},{opacity:0,transform:`translateX(${-direction * 16}px)`}],options));
          animations.push(incoming.animate([{transform:`translateX(${direction * 16}px)`},{transform:'translateX(0)'}],options));
          animations.forEach(animation => motions.add(animation));
          await Promise.all(animations.map(animation => animation.finished.catch(() => {})));
        }
        animations.forEach(animation => motions.delete(animation));
        if (token !== generation) return;
        if (fullscreen) outgoing.hidden = true;
        current = target; transitioning = false; go(gallery, current); announce(); preload();
      }
      function bindGestures(surface, fullscreen) {
        let last = 0, magnitude = 0, direction = 0, total = 0, consumed = false, decayed = false, touch = null;
        const reset = () => { last = magnitude = direction = total = 0; consumed = decayed = false; touch = null; };
        gestures.push(reset);
        surface.addEventListener('wheel', event => {
          if ((!fullscreen && stackedGallery()) || event.ctrlKey || event.metaKey || event.altKey) return;
          // Vertical input belongs to the page, including diagonal vertical intent.
          if (Math.abs(event.deltaX) < 3 || Math.abs(event.deltaX) <= Math.abs(event.deltaY) * 1.3) return;
          const dominant = event.deltaX;
          const delta = dominant * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? surface.clientHeight : 1);
          if (!delta) return;
          if (!fullscreen && photos.length < 2) return;
          const sign = Math.sign(delta), amount = Math.abs(delta), now = performance.now();
          const beyond = (current === 0 && sign < 0) || (current === photos.length - 1 && sign > 0);
          if (!fullscreen && beyond && !transitioning) { reset(); return; }
          event.preventDefault();
          const fresh = now - last >= 120 || sign !== direction || (decayed && amount >= 24 && amount > magnitude * 2.5);
          if (fresh) { total = 0; consumed = false; decayed = false; }
          if (amount < 12) decayed = true;
          last = now; magnitude = amount; direction = sign;
          if (transitioning || consumed) { consumed = true; return; }
          if (amount < 3) return;
          total += delta;
          if (Math.abs(total) >= 48) { consumed = true; navigate(current + sign); }
        }, {passive:false,signal:events.signal});
        on(surface, 'touchstart', event => {
          touch = (fullscreen || !stackedGallery()) && event.touches.length === 1 && !transitioning ? {x:event.touches[0].clientX,y:event.touches[0].clientY} : null;
        });
        on(surface, 'touchend', event => {
          if (!touch || event.touches.length) { touch = null; return; }
          const point = event.changedTouches[0], dx = point.clientX - touch.x, dy = point.clientY - touch.y;
          touch = null;
          if (Math.abs(dx) >= 40 && Math.abs(dx) > Math.abs(dy) * 1.3) navigate(current + (dx < 0 ? 1 : -1));
        });
        on(surface, 'touchcancel', () => { touch = null; });
      }
      thumbs.forEach((thumb, i) => on(thumb, 'click', event => { event.preventDefault(); navigate(i, true); if (stackedGallery()) photos[i]?.scrollIntoView({block:'nearest',behavior:reduced.matches?'instant':'smooth'}); }));
      photos.forEach((photo, i) => on(photo, 'click', event => {
        if (root.dataset.viewerEnabled === 'false') { event.preventDefault(); return; }
        if (!viewer?.showModal) return;
        event.preventDefault();
        if (viewer.open || viewerMotion?.busy) return;
        resetViewer(i); viewerMotion.open(photo); preload();
      }));
      viewerMotion = window.NeutralsImageViewer(viewer, {signal:events.signal,reduced,
        currentFrame:() => frames[current],destinations:() => [...photos,...thumbs],
        settle:() => resetViewer(current),focusReturn:element => returnTargets.set(viewer,element)
      });
      on(viewer, 'close', () => resetViewer(current));
      on(next, 'click', () => navigate(current + 1));
      on(previous, 'click', () => navigate(current - 1));
      [viewer, gallery].forEach(surface => on(surface, 'keydown', event => {
        if (surface === gallery && stackedGallery()) return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault(); if (!event.repeat) navigate(current + (event.key === 'ArrowRight' ? 1 : -1));
        }
      }));
      bindGestures(viewer, true);
      bindGestures(gallery, false);
      on(events.signal, 'abort', () => { resetViewer(current); delete gallery.dataset.controlled; delete viewerImages.dataset.controlled; });
      resetViewer(current);
      const resize = new ResizeObserver(() => { if (!transitioning) go(gallery, current); });
      resize.observe(gallery);
      on(desktopGallery, 'change', () => resetViewer(current));
      on(events.signal, 'abort', () => resize.disconnect());
      if (photos.length === 1) { root.querySelector('[data-next]').hidden = true; root.querySelector('[data-previous]').hidden = true; }
    }
    // Theme Editor selection reveals disclosure content without persistent listeners.
    const editorOpened = new Set();
    on(document, 'shopify:block:select', event => {
      const block = event.target.closest?.('[data-product-block]');
      if (!block || !root.contains(block)) return;
      const details = block.querySelector('details');
      if (details && !details.open) { details.open = true; editorOpened.add(details); }
      block.scrollIntoView({block:'nearest',behavior:'instant'});
    });
    on(document, 'shopify:block:deselect', event => {
      const details = event.target.closest?.('[data-product-block]')?.querySelector('details');
      if (editorOpened.has(details)) { details.open = false; editorOpened.delete(details); }
    });
    const info = root.querySelector('.np__info');
    if (info) {
      let header = null;
      const measurePanel = () => {
        const top = Math.ceil(header?.getBoundingClientRect().height || 0) + 16;
        root.style.setProperty('--np-sticky-top', `${top}px`);
      };
      const panelObserver = new ResizeObserver(measurePanel);
      const observeHeader = () => {
        const nextHeader = document.querySelector('.header');
        if (header !== nextHeader) {
          if (header) panelObserver.unobserve(header);
          header = nextHeader;
          if (header) panelObserver.observe(header);
        }
        measurePanel();
      };
      panelObserver.observe(info);
      on(document, 'shopify:section:load', observeHeader);
      on(window, 'resize', measurePanel);
      on(events.signal, 'abort', () => panelObserver.disconnect());
      observeHeader();
    }
    update(); root.dataset.enhanced = 'true';
    instances.set(root, () => { events.abort(); request?.abort(); exits.forEach(animation => animation.cancel()); exits.clear(); dialogs.forEach(d => { if (d.open) d.close(); }); root.removeAttribute('data-enhanced'); instances.delete(root); });
  }
  const scan = () => document.querySelectorAll('[data-product-page]').forEach(init);
  document.addEventListener('shopify:section:load', scan);
  document.addEventListener('shopify:section:unload', event => instances.forEach((destroy, root) => { if (event.target.contains(root)) destroy(); }));
  window.addEventListener('pagehide', () => instances.forEach(destroy => destroy()));
  window.addEventListener('pageshow', scan);
  scan();
})();
